import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicApi } from '../services/api';
import { Spinner } from '../components/ui/spinner';
import { ChevronLeft, ChevronRight, X, Play, Pause } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';

interface Slide {
  id: number;
  image_path: string;
  narration: string;
  position: number;
}

interface Story {
  id: number;
  title: string;
  one_line_brief: string;
  category_name: string;
  subcategory_name: string;
  tags: Array<{ id: number; name: string }>;
  slides: Slide[];
}

export default function PublicStory() {
  const { publicToken } = useParams<{ publicToken: string }>();
  const navigate = useNavigate();
  
  const [story, setStory] = useState<Story | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (publicToken) {
      loadStory();
    }
  }, [publicToken]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && story && currentSlide < story.slides.length - 1) {
      interval = setInterval(() => {
        setCurrentSlide(prev => prev + 1);
      }, 5000);
    } else if (isPlaying && currentSlide >= story!.slides.length - 1) {
      setIsPlaying(false);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentSlide, story]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (showControls) {
      timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [showControls, currentSlide]);

  const loadStory = async () => {
    try {
      const response = await publicApi.getStory(publicToken!);
      setStory(response.data.story);
      setLoading(false);
    } catch (error: any) {
      console.error('Failed to load story:', error);
      setError(error.response?.data?.error || 'Failed to load story');
      setLoading(false);
    }
  };

  const handlePrevious = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
      setShowControls(true);
    }
  }, [currentSlide]);

  const handleNext = useCallback(() => {
    if (story && currentSlide < story.slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      setShowControls(true);
    }
  }, [currentSlide, story]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    } else if (e.key === 'Escape') {
      navigate('/');
    } else if (e.key === ' ') {
      e.preventDefault();
      setIsPlaying(!isPlaying);
    }
  }, [handlePrevious, handleNext, navigate, isPlaying]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };

    const diffX = touchStart.x - touchEnd.x;
    const diffY = Math.abs(touchStart.y - touchEnd.y);

    if (Math.abs(diffX) > 50 && diffY < 100) {
      if (diffX > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }

    setTouchStart(null);
  };

  const handleMouseMove = () => {
    setShowControls(true);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <Spinner size="lg" className="text-white" />
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Story Not Found</h2>
          <p className="text-gray-300 mb-6">{error || 'This story is not available.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-white text-black rounded-md hover:bg-gray-200 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentSlideData = story.slides[currentSlide];
  const progress = ((currentSlide + 1) / story.slides.length) * 100;

  return (
    <div 
      className="fixed inset-0 bg-black overflow-hidden"
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          src={getImageUrl(currentSlideData.image_path)}
          alt={`Slide ${currentSlide + 1}`}
          className="max-w-full max-h-full object-contain"
        />
      </div>

      <div 
        className={`absolute inset-0 pointer-events-none transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-6 pointer-events-auto">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-white text-2xl font-bold mb-1">{story.title}</h1>
              {story.one_line_brief && (
                <p className="text-gray-300 text-sm">{story.one_line_brief}</p>
              )}
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 pointer-events-auto">
          {currentSlideData.narration && (
            <div className="mb-6 max-w-3xl mx-auto">
              <p className="text-white text-lg leading-relaxed text-center">
                {currentSlideData.narration}
              </p>
            </div>
          )}

          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <button
              onClick={handlePrevious}
              disabled={currentSlide === 0}
              className={`text-white p-3 rounded-full transition-all ${
                currentSlide === 0
                  ? 'opacity-30 cursor-not-allowed'
                  : 'hover:bg-white/20'
              }`}
            >
              <ChevronLeft className="h-8 w-8" />
            </button>

            <div className="flex-1 mx-8">
              <div className="flex items-center gap-4 mb-2">
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                </button>
                <div className="flex-1">
                  <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-white transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
                <span className="text-white text-sm">
                  {currentSlide + 1} / {story.slides.length}
                </span>
              </div>
            </div>

            <button
              onClick={handleNext}
              disabled={currentSlide === story.slides.length - 1}
              className={`text-white p-3 rounded-full transition-all ${
                currentSlide === story.slides.length - 1
                  ? 'opacity-30 cursor-not-allowed'
                  : 'hover:bg-white/20'
              }`}
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          </div>

          <div className="mt-4 text-center">
            <div className="flex flex-wrap gap-2 justify-center">
              {story.tags.map(tag => (
                <span
                  key={tag.id}
                  className="inline-block px-3 py-1 bg-white/20 text-white text-xs rounded-full"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={handlePrevious}
          disabled={currentSlide === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-3 rounded-full hover:bg-white/20 transition-all pointer-events-auto disabled:opacity-0"
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-12 w-12" />
        </button>

        <button
          onClick={handleNext}
          disabled={currentSlide === story.slides.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-3 rounded-full hover:bg-white/20 transition-all pointer-events-auto disabled:opacity-0"
          aria-label="Next slide"
        >
          <ChevronRight className="h-12 w-12" />
        </button>
      </div>

      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-none">
        {story.slides.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setCurrentSlide(index);
              setShowControls(true);
            }}
            className={`w-2 h-2 rounded-full transition-all pointer-events-auto ${
              index === currentSlide
                ? 'bg-white w-8'
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}